// MIDI note payloads — the pure half of MIDI clip sync.
//
// Lives in shared/ for the same reason protocol.ts does: the extension, the
// JamRoom Durable Object, viz and jam-sim all have to agree on (a) the wire
// shape of a note list and (b) the digest that says whether two lists are the
// same content. No `node:` imports, no SDK, no crypto APIs — the digest is a
// plain FNV-1a over the canonical string so every runtime computes the same
// value without a hashing seam.
//
// Why a digest at all: the SDK exposes `MidiClip.notes` only as a whole-array
// get/set, so a note edit changes no geometry — and the sync engine's echo
// short-circuit (apply-ops) and content trigger (compile-intents) are both
// geometry-based. `notesDigest` plays the role `audioKey` plays for audio
// clips: "the content behind this geometry changed".

/** One note as it travels. Times in beats, relative to the clip's start on
 * the arrangement (post-flatten — see `flattenMidiClip`). */
export interface WireNote {
  pitch: number;
  startTime: number;
  duration: number;
  velocity: number;
  muted?: boolean;
  probability?: number;
  velocityDeviation?: number;
  releaseVelocity?: number;
}

/** Live's defaults for the optional fields; canonical form omits a field that
 * holds its default so an SDK read that fills defaults and one that doesn't
 * digest identically. */
export const NOTE_DEFAULTS = {
  velocity: 100,
  probability: 1,
  velocityDeviation: 0,
  releaseVelocity: 64,
} as const;

/** Hard cap on notes per clip on the wire (~250 KB of JSON at the top). A
 * clip over the cap ships WITHOUT notes and says so (`notesOmitted`); it is
 * never truncated silently. */
export const MAX_NOTES_PER_CLIP = 4000;

/** Round to 4 decimals — the sync engine's ASSERT_EPS precision. Duplicated
 * from sync/canonical.ts rather than imported so this module stays leaf. */
function r4(x: number): number {
  return Math.round(x * 1e4) / 1e4;
}

/**
 * Deterministic form of a note list: rounded, defaults dropped, `selected`
 * (UI state) gone, sorted. Two lists that play identically canonicalise to the
 * same array, byte for byte.
 */
export function canonicalNotes(notes: readonly WireNote[]): WireNote[] {
  const out: WireNote[] = notes.map((n) => {
    const c: WireNote = {
      pitch: Math.round(n.pitch),
      startTime: r4(n.startTime),
      duration: r4(n.duration),
      velocity: r4(n.velocity ?? NOTE_DEFAULTS.velocity),
    };
    if (n.muted) c.muted = true;
    if (n.probability !== undefined && r4(n.probability) !== NOTE_DEFAULTS.probability) {
      c.probability = r4(n.probability);
    }
    if (n.velocityDeviation !== undefined && r4(n.velocityDeviation) !== NOTE_DEFAULTS.velocityDeviation) {
      c.velocityDeviation = r4(n.velocityDeviation);
    }
    if (n.releaseVelocity !== undefined && r4(n.releaseVelocity) !== NOTE_DEFAULTS.releaseVelocity) {
      c.releaseVelocity = r4(n.releaseVelocity);
    }
    return c;
  });
  out.sort(
    (a, b) =>
      a.startTime - b.startTime ||
      a.pitch - b.pitch ||
      a.duration - b.duration ||
      a.velocity - b.velocity ||
      Number(a.muted ?? false) - Number(b.muted ?? false),
  );
  return out;
}

/** The canonical string the digest is taken over. Exposed for tests. */
export function canonicalNotesString(notes: readonly WireNote[]): string {
  return JSON.stringify(
    canonicalNotes(notes).map((n) => [
      n.pitch,
      n.startTime,
      n.duration,
      n.velocity,
      n.muted ? 1 : 0,
      n.probability ?? NOTE_DEFAULTS.probability,
      n.velocityDeviation ?? NOTE_DEFAULTS.velocityDeviation,
      n.releaseVelocity ?? NOTE_DEFAULTS.releaseVelocity,
    ]),
  );
}

/**
 * Content digest of a note list: 16 hex chars — two independent 32-bit FNV-1a
 * passes over the canonical string. Not cryptographic and not meant to be;
 * it only has to be stable across runtimes and unlikely to collide within one
 * clip's edit history.
 */
export function notesDigest(notes: readonly WireNote[]): string {
  const s = canonicalNotesString(notes);
  return fnv1a32(s, 0x811c9dc5).toString(16).padStart(8, "0") +
    fnv1a32(s, 0x01000193 ^ 0x811c9dc5).toString(16).padStart(8, "0");
}

function fnv1a32(s: string, seed: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export interface MidiClipGeometry {
  /** Arrangement length of the clip, in beats. */
  duration: number;
  startMarker: number;
  endMarker: number;
  looping: boolean;
  loopStart: number;
  loopEnd: number;
}

/** One pass of a clip's playback: clip-internal beats [from, to) sound at
 * arrangement offset `at` (beats from the clip's start). */
export interface PlaybackWindow {
  from: number;
  to: number;
  at: number;
}

/**
 * The windows of clip-internal time Live plays over a clip's arrangement
 * length, in order — the playback model `flattenMidiClip` documents below,
 * stated once so an audio player (the viz schedules a looped clip's object
 * segment by segment) and the MIDI flattener can never disagree. The last
 * window may run past the arrangement length; callers clip it to
 * `duration - at`.
 */
export function playbackWindows(geom: MidiClipGeometry): PlaybackWindow[] {
  const total = Math.max(0, geom.duration);
  const windows: PlaybackWindow[] = [];
  if (total === 0) return windows;
  if (!geom.looping || !(geom.loopEnd > geom.loopStart)) {
    windows.push({ from: geom.startMarker, to: geom.startMarker + total, at: 0 });
    return windows;
  }
  let at = 0;
  const first = geom.loopEnd - geom.startMarker;
  if (first > 0) {
    windows.push({ from: geom.startMarker, to: geom.loopEnd, at });
    at += first;
  }
  const cycle = geom.loopEnd - geom.loopStart;
  // Guard against a degenerate loop length blowing up the unroll.
  const maxCycles = Math.ceil(total / Math.max(cycle, 1e-3)) + 1;
  for (let i = 0; at < total && i < maxCycles; i++) {
    windows.push({ from: geom.loopStart, to: geom.loopEnd, at });
    at += cycle;
  }
  return windows;
}

/**
 * The notes an UNLOOPED clip of the same arrangement length would need to
 * play the same thing. Receivers can't set loop range or markers (the SDK has
 * setters for `looping` and `notes` only), so the sender sends this instead
 * and the receiver builds `createMidiClip(startTime, duration)` + `notes`.
 *
 * Playback model (Live's, as understood pre-spike — PR0 item 3 pins it):
 *   • times in `notes` are clip-internal beats, where the clip's start marker
 *     is what plays at the clip's arrangement start;
 *   • not looping: the window [startMarker, startMarker + duration) plays once;
 *   • looping: [startMarker, loopEnd) plays first, then [loopStart, loopEnd)
 *     repeats until the arrangement length is used up;
 *   • a note starting inside a window plays from its start and is cut at the
 *     window's end; a note that started before the window does not retrigger.
 * Output times are relative to the clip's arrangement start, in [0, duration).
 */
export function flattenMidiClip(notes: readonly WireNote[], geom: MidiClipGeometry): WireNote[] {
  const total = Math.max(0, geom.duration);
  if (total === 0 || notes.length === 0) return [];

  const out: WireNote[] = [];
  for (const w of playbackWindows(geom)) {
    const span = Math.min(w.to - w.from, total - w.at);
    if (span <= 0) continue;
    const windowEnd = w.from + span;
    for (const n of notes) {
      if (n.startTime < w.from || n.startTime >= windowEnd) continue;
      const start = w.at + (n.startTime - w.from);
      const duration = Math.min(n.duration, windowEnd - n.startTime);
      if (duration <= 0) continue;
      out.push({ ...n, startTime: start, duration });
    }
  }
  return out;
}
